// @flow
// $FlowFixMe
import React, { Fragment, useState } from "react";
import { noop } from "lodash";
import { CSSTransition } from "react-transition-group";
import { stopTouchEvent } from "@tvg/atomic-ui/_templates/ModalV2/utils";
import { useModalV2Resize } from "@tvg/custom-hooks";

import ModalHeader from "../../_organisms/ModalHeader";
import { calcVideoContainerHeight } from "../../_organisms/Video";
import {
  Overlay,
  Transitions,
  Container,
  Content,
  Footer
} from "./styled-components";
import type { ModalProps, TransitionState } from "./types";
import { ModalDefaultProps } from "./defaultProps";

const Modal = (props: ModalProps) => {
  const [transition, setTransition] = useState<TransitionState>("exited");

  useModalV2Resize(props.isOpen);

  const renderOverlay = () =>
    transition !== "exited" ? (
      <Overlay
        data-qa-label={props.qaLabel && `${props.qaLabel}-overlay`}
        onTouchStart={stopTouchEvent}
        onTouchCancel={stopTouchEvent}
        onTouchEnd={stopTouchEvent}
        onTouchMove={stopTouchEvent}
        {...(typeof props.onOverlayClick === "function"
          ? {
              onClick: props.onOverlayClick
            }
          : {
              onClick: props.onClose
            })}
        opening={transition === "enter"}
        exiting={transition === "exiting"}
        exited={transition === "exited"}
      />
    ) : null;

  const renderTransparentOverlay = () =>
    transition !== "exited" ? (
      <Overlay
        data-qa-label={props.qaLabel && `${props.qaLabel}-overlay`}
        onTouchStart={stopTouchEvent}
        onTouchCancel={stopTouchEvent}
        onTouchEnd={stopTouchEvent}
        onTouchMove={stopTouchEvent}
        {...(typeof props.onOverlayClick === "function"
          ? {
              onClick: props.onOverlayClick
            }
          : {
              onClick: props.onClose
            })}
        opening={transition === "enter"}
        exiting={transition === "exiting"}
        exited={transition === "exited"}
        isTransparent
      />
    ) : null;

  const renderTopChildren = () => {
    if (props.modalTopChildren !== noop) {
      return props.modalTopChildren();
    }
    return null;
  };

  const renderTitle = () => {
    if (
      props.hasCloseWithoutHeader ||
      (props.hasHeader && (props.title || props.icon))
    ) {
      return (
        <ModalHeader
          titleBadgeNumber={props.titleBadgeNumber}
          displayZeroTitleBadge={props.displayZeroTitleBadge}
          isSubTitleUppercase={props.isSubTitleUppercase}
          isCapitalized={props.isCapitalized}
          title={props.title}
          subtitle={props.subtitle}
          titleType={props.titleType}
          hasClose={props.hasClose}
          useCloseLabel={props.useCloseLabel}
          closeLabel={props.closeLabel}
          onClose={props.onClose}
          onBack={props.onBack}
          hasRoundedCorners={props.hasRoundedCorners}
          hasCloseWithoutHeader={props.hasCloseWithoutHeader}
          hasShadow={props.hasShadow}
          icon={props.icon}
          isFullHeight={props.isFullHeight}
          hideFixedBanner={props.hideFixedBanner}
          titleAlign={props.titleAlign}
        />
      );
    }

    return null;
  };

  const renderContainer = (transitionState: TransitionState) => (
    <Container
      qaLabel="modal-container"
      onTouchStart={stopTouchEvent}
      onTouchCancel={stopTouchEvent}
      onTouchEnd={stopTouchEvent}
      onTouchMove={stopTouchEvent}
      role="presentation"
      onClick={(e) => {
        e.stopPropagation();
      }}
      isFullWidth={props.isFullWidth}
      offset={{ top: props.offsetTop, bottom: props.offsetBottom }}
      {...(props.qaLabel ? { qaLabel: props.qaLabel } : {})}
    >
      {transition !== "exiting" && renderTopChildren()}
      {renderTitle()}
      <Content
        data-qa-label="modal-content"
        isContentTransparent={props.isContentTransparent}
        isNavigation={props.titleType === "navigation"}
        roundedCorners={props.hasBottomCorners}
        hasSafeArea={props.hasSafeArea && !props.footer}
      >
        {typeof props.children === "function" &&
          props.children(transitionState, props.onClose)}
      </Content>
      {props.footer && (
        <Footer hasSafeArea={props.hasSafeArea}>{props.footer}</Footer>
      )}
    </Container>
  );

  const transitionProps = {
    appear: true,
    mountOnEnter: true,
    unmountOnExit: true,
    timeout: 300,
    in: props.isOpen
  };

  const transitionStates = {
    onEnter: () => setTransition("enter"),
    onExiting: () => setTransition("exiting"),
    onExited: () => setTransition("exited")
  };

  const Transition = Transitions[props.animation];
  const headerOffsetHeight = 48;

  return (
    <Fragment>
      {props.hasOverlay && renderOverlay()}
      {!props.hasOverlay && renderTransparentOverlay()}
      <CSSTransition
        {...transitionProps}
        {...transitionStates}
        classNames={props.animation}
      >
        {(state) => (
          <Transition
            data-qa-label={`modal-${props.animation}-transition`}
            type={props.animation}
            isFullWidth={props.isFullWidth && !props.isFluidWidth}
            isFluidWidth={props.isFluidWidth && !props.isFullWidth}
            isFullHeight={props.isFullHeight}
            offset={{
              top: props.hasVideoOffset
                ? calcVideoContainerHeight() + headerOffsetHeight // video height + header height
                : props.offsetTop,
              bottom: props.offsetBottom,
              left: props.offsetLeft,
              right: props.offsetRight
            }}
            fixIosKeyboard={props.fixIosKeyboard}
            device={props.device}
          >
            {renderContainer(state)}
          </Transition>
        )}
      </CSSTransition>
    </Fragment>
  );
};

Modal.defaultProps = ModalDefaultProps;

export default Modal;
